import {
    Component, Mixins,
} from 'vue-property-decorator';
import Classes from '@components/shared/mixins/classes';
import Hybrid from '@components/shared/mixins/hybrid';
import config from '@components/shared/config';
import CoreMixin from '@nodes/mixins/Core';
import { set } from 'lodash/object';
import { YSelect } from '@components/index';
import Props from './props';

/**
 * @name Division
 */
@Component({
    components: {
        YSelect,
    },
})
class YDivision extends Mixins(Props, Classes, Hybrid, CoreMixin) {
    /**
     * Props list that active by developer
     */
    activeCommonProps: Array<string> = ['color', 'size'];

    /**
     * The css parent class of the component
     */
    parentClass: string = this.componentClass(this.baseClass, config.prefix.division);

    model = {}

    selectedDivs = {}

    ajaxFilter = {
        continent: 'continents_id',
        country  : 'countries_id',
        province : 'provinces_id',
        city     : 'cities_id',
    }

    hierarchy = {
        continent: {
            loading: false,
            options: [],
        },

        country: {
            loading: false,
            options: [],
        },

        province: {
            loading: false,
            options: [],
        },

        city: {
            loading: false,
            options: [],
        },

        neighborhood: {
            loading: false,
            options: [],
        },
    }

    currentDiv = null

    divs = {
        continent   : [],
        country     : [],
        province    : [],
        city        : [],
        neighborhood: [],
    }

    /**
     * Create Common Props for the division.
     * 
     * @returns {object} - List of props
     */
    get commonProps(): Record<string, any> {
        const commonProps: Record<string, any> = {
            baseClass: this.baseClass,
        };
        this.activeCommonProps.forEach((item) => {
            if (this.element && this.element[item]) {
                commonProps[item] = this.element[item];
            } else {
                commonProps[item] = this[item];
            }
        });
        return commonProps;
    }

    /**
     * Create classes array for the division.
     * 
     * @returns {Array} - Array of classes
     */
    get classes(): Array<string> {
        const cls: Array<string> = [];
        cls.push(...this.commonClasses('Division', this.commonProps, this.baseClass || config.prefix.division));
        return cls;
    }

    /**
     * Gets an array of hierarchy keys
     *
     * @returns {string[]}
     */
    get hierarchyArray(): string[] {
        return Object.keys(this.hierarchy);
    }

    /**
     * Gets an list of visible Divisions
     *
     * @returns {*}
     */
    get visibleDivs(): any {
        const start = this.startIndex();
        const end = this.endIndex();

        return this.hierarchyArray.slice(start, end);
    }

    /**
     * Get placeholder
     * 
     * @returns {*}
     */
    get getPlaceholder(): any {
        return this.placeholder || this.$t('forms.select.placeholder.default');
    }

    /**
     * @inheritDoc
     */
    created() {
        this.currentDiv = this.startIndex();

        if (this.value) {
            this.model = this.value;
            this.selectedDivs = { ...this.model };
        }

        if (!this.value) {
            this.$emit('input', this.model);
        }

        this.getData();
    }

    /**
     * gets first field index in hierarchyArray
     *
     * @returns {number}
     */
    startIndex(): number {
        const start = this.hierarchyArray.findIndex((div) => !!this.$props[div]);

        return start > -1 ? start + 1 : 0;
    }

    /**
     * gets last field index in hierarchyArray
     *
     * @returns {*}
     */
    endIndex(): any {
        const end = this.hierarchyArray.findIndex((div) => div === this.limit);

        return end > -1 ? end + 1 : this.hierarchyArray.length;
    }

    /**
     * emit value of chosen field
     *
     * @param {*} value
     * @param {string} div
     */
    emitValue(value: any, div: string) {
        this.model[div] = value;

        if (this.currentDiv !== this.hierarchyArray.indexOf(div)) {
            this.currentDiv = this.hierarchyArray.indexOf(div);
        }

        this.currentDiv += 1;

        if (this.currentDiv < this.endIndex() && value) {
            this.getData();
        }

        this.removeModel(this.hierarchyArray.indexOf(div));
        if (!value) {
            this.$emit('remove');
        }
    }

    /**
     * gets divisions list according to filters
     *
     * @param {*} type
     * @param {*} filter
     */
    getOptions(type: string, filter: { [x: number]: any[]; }) {
        const params = {
            types    : [type],
            sort     : `title_${this.$i18n.locale}`,
            order    : 'asc',
            including: [
                'id',
                `title_${this.$i18n.locale}`,
            ],
            ...filter,
        };
        this.hierarchy[type].loading = true;
        const titleLocale = `title_${this.$i18n.locale}`;
        this.$services.list('division', params).then((response: { data: { results: any[]; }; }) => {
            const options = response.data.results.map((item) => ({
                value: item.id,
                label: item[titleLocale],
            }));
            this.$set(this.hierarchy[type], 'options', options);
        }).catch((error: object) => {
            this.handleError(error);
        }).then(() => {
            this.hierarchy[type].loading = false;
        });
    }

    /**
     * generate filters for divisions list and gets list
     */
    getData() {
        const first = this.hierarchyArray[this.currentDiv];
        const before = this.currentDiv ? this.hierarchyArray[this.currentDiv - 1] : 0;
        const beforeVal = this.$props[before] || this.model[before];

        const filter = { [this.ajaxFilter[before]]: [beforeVal] };
        this.getOptions(first, filter);
    }

    /**
     * emit empty value if a higher level field updates
     *
     * @param {number} changedIndex
     */
    removeModel(changedIndex: number) {
        const done = this.hierarchyArray[changedIndex];
        if (this.selectedDivs && this.selectedDivs.hasOwnProperty(done)) {
            delete this.selectedDivs[done];
        } else {
            Object.keys(this.model).forEach((item) => {
                if (this.hierarchyArray.indexOf(item) > changedIndex) {
                    if (this.model.hasOwnProperty(item)) {
                        delete this.model[item];
                    }
                    this.hierarchy[item].options = [];
                    this.divs[item] = [];
                }
            });
        }
        this.$emit('input', this.model);
    }

    /**
     * showSelect
     *
     * @param {*} div
     * @param {*} index
     * @returns {boolean}
     */
    showSelect(div: any, index: any): boolean {
        return !this.hideEmpties || (this.hierarchy[div].options && this.hierarchy[div].options.length && this.currentDiv >= index);
    }

    /**
     * Selected Div
     *
     * @param {*} data
     * @param {*} div
     */
    emitSelectedDiv(data: any, div: any) {
        const pushable = this.allowEmpty ? this.isMulti(div) : false;
        if (pushable) {
            const duplicate = false;
            if (!duplicate) {
                this.divs[div].push(data);
            }
        } else {
            this.divs[div] = [];
            this.divs[div].push(data);
        }
        this.$emit('divs', this.divs);
    }

    /**
     * Clear Selected Divs
     */
    clearSelectedDiv() {
        set(this, 'divs', {
            continent    : [],
            country      : [],
            province     : [],
            city         : [],
            neighbourhood: [],
        });
    }

    /**
     * Check whether the div is multi
     *
     * @param {string} div
     * @returns {boolean}
     */
    isMulti(div: any): boolean {
        return this.multi.includes(div);
    }
}

export default YDivision;
